import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosRequest from '@/api';
import { AdvertiserData } from '@/@types/AdvertiserData';
import * as AD from './index.styled';
import { ResData } from '@/@types';
import { Form, Input, Button, Select, Typography, Modal, message, Upload, Alert } from 'antd';
import { ExclamationCircleOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import type { RcFile } from 'antd/es/upload/interface';
import { ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { confirm } = Modal;

const statusOptions = [
  { label: '승인', value: 'a' },
  { label: '대기', value: 'p' },
  { label: '반려', value: 'd' },
];

const categoryOptions = [
  { label: '대행사', value: 'a' },
  { label: '고객사', value: 'c' },
];

const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

function AdvertiserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [advertiser, setAdvertiser] = useState<AdvertiserData | null>(null);
  const [uploading, setUploading] = useState(false);
  const [upperAdCorpOptions, setUpperAdCorpOptions] = useState<{ label: string; value: string }[]>([]);
  const [upperAdCorpName, setUpperAdCorpName] = useState<string | null>(null);
  const [showStatusAlert, setShowStatusAlert] = useState(false);
  const [showPartnerAlert, setShowPartnerAlert] = useState(false);
  const isEditMode = Boolean(id && id !== 'new'); // 명확하게 boolean 타입으로 변환
  const [form] = Form.useForm();

  useEffect(() => {
    if (isEditMode) {
      const fetchAdvertiser = async () => {
        try {
          const response = await axiosRequest.requestAxios<ResData<AdvertiserData>>('get', `/advertiser/${id}`);
          setAdvertiser(response.data);
          fetchUpperAdvertisers(response.data.category, response.data.partner_id);
        } catch (error) {
          console.error('Error fetching advertiser:', error);
        }
      };

      fetchAdvertiser();
    } else {
      setAdvertiser({
        id: null,
        name: '',
        status: 'a',
        manager_name: '',
        manager_tel: '',
        manager_email: '',
        category: '',
        biz_no: '',
        ceo_name: '',
        biz_type: '',
        biz_item: '',
        address: '',
        biz_enroll_docs: '',
        confirmed_date: '',
        confirmed_user: '',
        updated_at: '',
        upper_ad_corp: '',
        partner_id: '',
        ad_key: null,
      });
    }
  }, [id, isEditMode]);

  const fetchUpperAdvertisers = async (category: string, partner_id?: string) => {
    try {
      const response = await axiosRequest.requestAxios<ResData<{ name: string; id: string }[]>>(
        'get',
        `/partner_company/${category}`
      );
      setUpperAdCorpOptions(response.data.map(item => ({ label: item.name, value: item.id })));

      if (partner_id) {
        const selectedAdCorp = response.data.find(item => item.id === partner_id);
        if (selectedAdCorp) {
          setUpperAdCorpName(selectedAdCorp.name);
        }
      }
    } catch (error) {
      console.error('Error fetching upper advertisers:', error);
      message.error('상위 대행사 정보를 가져오는 데 실패했습니다.');
    }
  };

  useEffect(() => {
    if (advertiser?.category) {
      fetchUpperAdvertisers(advertiser.category, advertiser.partner_id);
    }
  }, [advertiser?.category]);

  const handleCategoryChange = (value: string) => {
    form.setFieldsValue({ partner_id: undefined }); // 상위 대행사 필드 초기화
    fetchUpperAdvertisers(value);
  };

  const handleDelete = () => {
    if (!id) return;

    confirm({
      title: '정말 삭제하시겠습니까? 광고주 정보가 삭제되면 캠페인 정보도 삭제됩니다.',
      icon: <ExclamationCircleOutlined />,
      content: '삭제하시겠습니까?',
      onOk: async () => {
        try {
          await axiosRequest.requestAxios('delete', `/advertiser/${id}`);
          message.success('삭제되었습니다.');
          navigate('/admin/advertiser');
        } catch (error) {
          console.error('Error deleting advertiser:', error);
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleSave = async (values: any) => {
    const biz_no = `${values.biz_no_part1}${values.biz_no_part2}${values.biz_no_part3}`;
    const updatedValues = {
      ...values,
      biz_no,
      manager_tel: `${values.manager_tel_part1}-${values.manager_tel_part2}-${values.manager_tel_part3}`,
      biz_enroll_docs: advertiser?.biz_enroll_docs,
      partner_id: advertiser?.category === 'a' ? '3' : values.partner_id, // 대행사일 경우 partner_id를 3으로 고정
    };

    // Remove part values from the payload
    delete updatedValues.biz_no_part1;
    delete updatedValues.biz_no_part2;
    delete updatedValues.biz_no_part3;
    delete updatedValues.manager_tel_part1;
    delete updatedValues.manager_tel_part2;
    delete updatedValues.manager_tel_part3;

    if (!updatedValues.biz_enroll_docs) {
      message.error('사업자등록증을 업로드해 주세요.');
      return;
    }

    try {
      if (isEditMode) {
        const response = await axiosRequest.requestAxios<ResData<AdvertiserData>>(
          'put',
          `/advertiser/${id}`,
          updatedValues
        );
        setAdvertiser(response.data);
        message.success('수정되었습니다.');
        navigate('/admin/advertiser');
      } else {
        const response = await axiosRequest.requestAxios<ResData<AdvertiserData>>('post', '/advertiser', updatedValues);
        setAdvertiser(response.data);
        message.success('등록되었습니다.');
        navigate('/admin/advertiser');
      }
    } catch (error) {
      console.error('Error saving advertiser:', error);
    }
  };

  const handleUpload = async (options: RcCustomRequestOptions) => {
    const { file, onSuccess, onError } = options;

    if (
      (file as RcFile).type !== 'application/pdf' &&
      (file as RcFile).type !== 'image/png' &&
      (file as RcFile).type !== 'image/jpeg' &&
      (file as RcFile).type !== 'image/bmp'
    ) {
      message.error('Only PDF, PNG, JPEG, BMP files are allowed.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file as RcFile);

    setUploading(true);
    try {
      const response = await axiosRequest.requestAxios<ResData<{ doc_url: string }>>('post', '/upload_bizdoc', formData);

      setAdvertiser(prevState => ({
        ...prevState,
        biz_enroll_docs: response.data.doc_url,
      }) as AdvertiserData);
      message.success('파일 업로드가 성공하였습니다.');
      if (onSuccess) {
        onSuccess(response.data, new XMLHttpRequest());
      }
    } catch (error) {
      message.error('파일 업로드가 실패하였습니다.');
      if (onError) {
        onError(error as ProgressEvent<EventTarget>);
      }
    } finally {
      setUploading(false);
    }
  };

  if (!advertiser) {
    return <AD.Wrapper>Loading...</AD.Wrapper>;
  }

  const initialBizNoParts = advertiser.biz_no
    ? [advertiser.biz_no.slice(0, 3), advertiser.biz_no.slice(3, 5), advertiser.biz_no.slice(5, 10)]
    : ['', '', ''];

  const initialTelParts = advertiser.manager_tel
    ? advertiser.manager_tel.includes('-')
      ? advertiser.manager_tel.split('-')
      : [advertiser.manager_tel.slice(0, 3), advertiser.manager_tel.slice(3, 7), advertiser.manager_tel.slice(7)]
    : ['', '', ''];

  const getCategoryLabel = (category: string) => {
    const categoryMap: { [key: string]: string } = { a: '대행사', c: '고객사' };
    return categoryMap[category] || category;
  };

  const getCategoryValue = (label: string) => {
    const labelMap: { [key: string]: string } = { 대행사: 'a', 고객사: 'c' };
    return labelMap[label] || label;
  };

  const handleGenerateKey = async () => {
    Modal.confirm({
      title: '정말 ad key를 새로 생성하시겠습니까?',
      onOk: async () => {
        try {
          const response = await axiosRequest.requestAxios<ResData<AdvertiserData>>('put', `/generate_ad_key/${advertiser?.id}`);

          if (response.data) {
            console.log('New ad_key:', response.data.ad_key);

            // 상태 업데이트
            setAdvertiser(prevData => {
              const updatedData = prevData ? { ...prevData, ad_key: response.data.ad_key } : prevData;

              // Form 필드 업데이트
              form.setFieldsValue({
                ad_key: response.data.ad_key, // 새로 생성된 ad_key를 폼 필드에 설정
              });

              return updatedData;
            });
          }
        } catch (error) {
          console.error('Error generating ad key:', error);
        }
      },
      onCancel() {
        console.log('ad key 생성 취소');
      },
    });
  };

  const handleCopyAdKey = () => {
    const adKey = form.getFieldValue('ad_key');
    if (adKey) {
      // 클립보드에 값 복사
      navigator.clipboard.writeText(adKey)
        .then(() => {
          message.success('ad key가 복사되었습니다.');
        })
        .catch(err => {
          console.error('Failed to copy ad key:', err);
          message.error('ad key 복사에 실패했습니다.');
        });
    }
  };

  return (
    <AD.Wrapper>
      <Title level={2}>{isEditMode ? '광고주 수정' : '광고주 등록'}</Title>
      <AD.StyledForm
        form={form}
        layout="vertical"
        initialValues={{
          ...advertiser,
          biz_no_part1: initialBizNoParts[0],
          biz_no_part2: initialBizNoParts[1],
          biz_no_part3: initialBizNoParts[2],
          manager_tel_part1: initialTelParts[0],
          manager_tel_part2: initialTelParts[1],
          manager_tel_part3: initialTelParts[2],
          category: getCategoryLabel(advertiser.category),
          partner_id: advertiser.partner_id,
          updated_at: advertiser.updated_at ? formatDate(advertiser.updated_at) : '',
          confirmed_date: advertiser.confirmed_date ? formatDate(advertiser.confirmed_date) : '', // 추가된 부분
        }}
        onFinish={handleSave}
      >
        <AD.StyledDivider>기본정보</AD.StyledDivider>
        <table>
          <tbody>
            <tr>
              <td>상태</td>
              <td>
                <Form.Item name="status" rules={[{ required: true, message: '상태를 선택해주세요.' }]}>
                  <Select
                    options={statusOptions}
                    placeholder="상태를 선택해주세요."
                    onChange={() => setShowStatusAlert(true)}
                  />
                </Form.Item>
                {showStatusAlert && (
                  <Alert
                    message="하위 고객사 및 관리 중인 캠페인의 상태도 변경됩니다."
                    type="info"
                    showIcon
                    style={{ marginTop: '10px' }}
                  />
                )}
              </td>
            </tr>
            <tr>
              <td>구분</td>
              <td>
                <Form.Item name="category" rules={[{ required: true, message: '구분을 선택해주세요.' }]}>
                  <Text>{getCategoryLabel(advertiser.category)}</Text>
                </Form.Item>
              </td>
            </tr>
            {advertiser.category === 'c' ? (
              <tr>
                <td>(상위) 대행사</td>
                <td>
                  <Form.Item
                    name="partner_id"
                    rules={[{ required: true, message: '상위 대행사를 선택해주세요.' }]}
                    extra={showPartnerAlert && (
                      <Alert
                        message="대행사 수정 후 신규 캠페인 생성 시 변경 대행사 하위로 생성됩니다."
                        type="info"
                        showIcon
                        style={{ marginTop: '10px' }}
                      />
                    )}
                  >
                    <Select
                      options={upperAdCorpOptions}
                      placeholder="상위 대행사를 선택해주세요."
                      onChange={value => {
                        form.setFieldsValue({ partner_id: value });
                        setShowPartnerAlert(true);
                      }}
                      value={advertiser.partner_id}
                    />
                  </Form.Item>
                </td>
              </tr>
            ) : (
              <tr>
                <td>(상위) 대행사</td>
                <td>
                  <Form.Item name="upper_ad_corp">
                    <Text>SKP</Text>
                  </Form.Item>
                </td>
              </tr>
            )}
            <tr>
              <td>기업명</td>
              <td>
                <Form.Item name="name" rules={[{ required: true, message: '기업명을 입력해주세요.' }]}>
                  <Input placeholder="기업명을 입력해주세요." />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td>사업자등록번호</td>
              <td>
                <AD.BizInputGroup>
                  <Form.Item name="biz_no_part1" noStyle rules={[{ required: true, message: '사업자등록번호를 입력해주세요.' }]}>
                    <Input style={{ width: '20%' }} maxLength={3} placeholder="000" />
                  </Form.Item>
                  <span>-</span>
                  <Form.Item name="biz_no_part2" noStyle rules={[{ required: true, message: '사업자등록번호를 입력해주세요.' }]}>
                    <Input style={{ width: '15%' }} maxLength={2} placeholder="00" />
                  </Form.Item>
                  <span>-</span>
                  <Form.Item name="biz_no_part3" noStyle rules={[{ required: true, message: '사업자등록번호를 입력해주세요.' }]}>
                    <Input style={{ width: '25%' }} maxLength={5} placeholder="00000" />
                  </Form.Item>
                </AD.BizInputGroup>
              </td>
            </tr>
            <tr>
              <td>대표자명</td>
              <td>
                <Form.Item name="ceo_name" rules={[{ required: true, message: '대표자명을 입력해주세요.' }]}>
                  <Input placeholder="대표자명을 입력해주세요." />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td>업종</td>
              <td>
                <Form.Item name="biz_type" rules={[{ required: true, message: '업종을 입력해주세요.' }]}>
                  <Input placeholder="업종을 입력해주세요." />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td>업태</td>
              <td>
                <Form.Item name="biz_item" rules={[{ required: true, message: '업태를 입력해주세요.' }]}>
                  <Input placeholder="업태를 입력해주세요." />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td>주소</td>
              <td>
                <Form.Item name="address" rules={[{ required: true, message: '주소를 입력해주세요.' }]}>
                  <Input placeholder="주소를 입력해주세요." />
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>

        <AD.StyledDivider>key 정보</AD.StyledDivider>
        <table>
          <tbody>
            <tr>
              <td>adKey</td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center' }}> {/* Flexbox로 정렬 */}
                  <Form.Item name="ad_key" style={{ marginRight: '10px', flex: 1 }}>
                    <Input readOnly={true} onClick={handleCopyAdKey} />
                  </Form.Item>
                  <Button
                    icon={<ReloadOutlined />} // 새로고침 아이콘
                    onClick={handleGenerateKey}
                  >
                    생성
                  </Button>
                </div>

              </td>
            </tr>
          </tbody>
        </table>

        <AD.StyledDivider>서류정보</AD.StyledDivider>
        <table>
          <tbody>
            <tr>
              <td>사업자등록증</td>
              <td>
                <AD.FlexContainer>
                  <Upload customRequest={handleUpload} showUploadList={false} accept=".pdf,.png,.jpeg,.jpg,.bmp">
                    <Button icon={<UploadOutlined />} loading={uploading}>
                      파일 선택
                    </Button>
                  </Upload>
                  <AD.StyledDownload href={advertiser.biz_enroll_docs} target="_blank" rel="noopener noreferrer">
                    {advertiser.biz_enroll_docs || 'pdf, png, jpeg, jpg, bmp 업로드 가능'}
                  </AD.StyledDownload>
                </AD.FlexContainer>
              </td>
            </tr>
          </tbody>
        </table>

        <AD.StyledDivider>담당자 정보</AD.StyledDivider>
        <table>
          <tbody>
            <tr>
              <td>이름</td>
              <td>
                <Form.Item name="manager_name" rules={[{ required: true, message: '담당자 이름을 입력해주세요.' }]}>
                  <Input placeholder="담당자 이름을 입력해주세요." />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td>휴대폰번호</td>
              <td>
                <AD.TelInputGroup>
                  <Form.Item name="manager_tel_part1" noStyle rules={[{ required: true, message: '휴대폰번호를 입력해주세요.' }]}>
                    <Input style={{ width: '20%' }} maxLength={3} placeholder="010" />
                  </Form.Item>
                  <span>-</span>
                  <Form.Item name="manager_tel_part2" noStyle rules={[{ required: true, message: '휴대폰번호를 입력해주세요.' }]}>
                    <Input style={{ width: '20%' }} maxLength={4} placeholder="1234" />
                  </Form.Item>
                  <span>-</span>
                  <Form.Item name="manager_tel_part3" noStyle rules={[{ required: true, message: '휴대폰번호를 입력해주세요.' }]}>
                    <Input style={{ width: '20%' }} maxLength={4} placeholder="5678" />
                  </Form.Item>
                </AD.TelInputGroup>
              </td>
            </tr>
            <tr>
              <td>이메일</td>
              <td>
                <Form.Item name="manager_email" rules={[{ required: true, message: '이메일을 입력해주세요.' }]}>
                  <Input placeholder="이메일을 입력해주세요." />
                </Form.Item>
              </td>
            </tr>
          </tbody>
        </table>

        {id !== 'new' && (
          <>
            <AD.StyledDivider>승인 정보</AD.StyledDivider>
            <table>
              <tbody>
                <tr>
                  <td>승인날짜</td>
                  <td>
                    <Form.Item name="confirmed_date">
                      <Input disabled value={advertiser.confirmed_date ? formatDate(advertiser.confirmed_date) : ''} />
                    </Form.Item>
                  </td>
                </tr>
                <tr>
                  <td>승인자</td>
                  <td>
                    <Form.Item name="confirmed_user">
                      <Input disabled value={advertiser.confirmed_user || ''} />
                    </Form.Item>
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        <AD.BtnContainer isEditMode={isEditMode}>
          {isEditMode ? (
            <>
              {/* <Button danger onClick={handleDelete} style={{ width: '80px' }}>
                삭제
              </Button> */}
              <Button type="primary" htmlType="submit" style={{ width: '80px' }}>
                수정하기
              </Button>
            </>
          ) : (
            <Button type="primary" htmlType="submit" style={{ width: '80px' }}>
              등록하기
            </Button>
          )}
        </AD.BtnContainer>
      </AD.StyledForm>
    </AD.Wrapper>
  );
}

export default AdvertiserDetail;
